import { useEffect, useMemo, useRef, useState } from "react";
import type { Contact } from "@veto/domain";
import { Avatar, IconChat, IconCopy, IconQr, IconSearch } from "@veto/ui";
import {
  copyText,
  fingerprintPreview,
  friendlyInviteError,
  inviteShareText,
  parseInviteText,
  type ParsedInvite,
} from "../messaging/inviteParse";
import { ModalShell } from "./ModalShell";
import { InviteScannerModal } from "./InviteScannerModal";

// FR-CON-001 — "as easy as WhatsApp": share one link (or QR), or paste/scan
// theirs, or pick someone you already have. No phone number, no directory.

const FOCUS_KEY = "veto.addContactTab";
const PENDING_INVITE_KEY = "veto.invitePaste";

/** Entry points ask the sheet to open on "share" (QR shown) or "add" (paste focused). */
export function preferAddContactTab(tab: "add" | "share" | "email"): void {
  try {
    sessionStorage.setItem(FOCUS_KEY, tab === "add" ? "add" : "share");
  } catch {
    /* ignore */
  }
}

function consume(key: string): string {
  try {
    const v = sessionStorage.getItem(key) ?? "";
    sessionStorage.removeItem(key);
    return v;
  } catch {
    return "";
  }
}

export function AddContactModal({
  myInviteLink,
  myDisplayName,
  contacts = [],
  initialInvitePaste = "",
  onClose,
  onAdd,
  onOpenContact,
}: {
  /** Our invite link (null while identity is locked). */
  myInviteLink: string | null;
  myDisplayName: string;
  contacts?: Contact[];
  initialInvitePaste?: string;
  onClose: () => void;
  onAdd: (name: string, hybrid: string, messaging: string, transport?: string) => Promise<void>;
  onOpenContact?: (contactId: string) => void;
}) {
  const [focus] = useState(() => consume(FOCUS_KEY) || "add");
  const [paste, setPaste] = useState(() => initialInvitePaste || consume(PENDING_INVITE_KEY));
  const [parsed, setParsed] = useState<ParsedInvite | null>(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(focus === "share");
  const [qr, setQr] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("");
  const pasteRef = useRef<HTMLTextAreaElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function apply(text: string, quiet = false) {
    if (!text.trim()) {
      setParsed(null);
      setErr(null);
      return;
    }
    try {
      const inv = parseInviteText(text);
      setParsed(inv);
      setName(inv.displayName);
      setErr(null);
    } catch (e) {
      setParsed(null);
      if (!quiet || text.trim().length > 40) setErr(friendlyInviteError(e));
    }
  }

  useEffect(() => {
    // Parse a pre-filled invite once on open (link / share target / launch queue).
    if (paste.trim()) apply(paste);
  }, []);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (focus === "add" && !parsed) {
      const t = window.setTimeout(() => pasteRef.current?.focus(), 60);
      return () => window.clearTimeout(t);
    }
  }, [focus, parsed]);

  useEffect(() => {
    if (!showQr || !myInviteLink) return;
    let cancelled = false;
    // Lazy-load the QR encoder (NFR-PERF-001). ECC "L" keeps the link at version ~32.
    void import("qrcode")
      .then((m) =>
        m.default.toDataURL(myInviteLink, {
          errorCorrectionLevel: "L",
          margin: 3,
          width: 640,
          color: { dark: "#05070C", light: "#FFFFFF" },
        }),
      )
      .then((url) => !cancelled && setQr(url))
      .catch(() => !cancelled && setQr(null));
    return () => {
      cancelled = true;
    };
  }, [showQr, myInviteLink]);

  const existing = useMemo(
    () =>
      parsed
        ? contacts.find((c) => c.messagingPublicKeyHex.toLowerCase() === parsed.messagingPublicKeyHex.toLowerCase())
        : undefined,
    [parsed, contacts],
  );

  const known = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return contacts
      .filter((c) => !c.blockedAt && c.requestState !== "pending")
      .filter((c) => !q || c.displayName.toLowerCase().includes(q))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [contacts, filter]);

  function onPasteChange(value: string) {
    setPaste(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => apply(value, true), 200);
  }

  async function share() {
    if (!myInviteLink) return;
    const text = inviteShareText(myDisplayName, myInviteLink);
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "Chat with me on Veto", text });
        return;
      } catch (e) {
        if ((e as { name?: string })?.name === "AbortError") return;
      }
    }
    await copy(text);
  }

  async function copy(value = myInviteLink ?? "") {
    if (!value) return;
    if (await copyText(value)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      setErr("Couldn't copy — long-press the link to copy it manually.");
    }
  }

  async function add() {
    if (!parsed || !name.trim() || adding) return;
    setAdding(true);
    setErr(null);
    try {
      await onAdd(name.trim(), parsed.hybridPublicKeyHex, parsed.messagingPublicKeyHex, parsed.transportPublicKeyHex);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't add this contact");
      setAdding(false);
    }
  }

  return (
    <ModalShell title="New chat" ariaLabel="Add contact" onClose={onClose} size="sheet" className="add-contact-card">
      <div className="stack add-contact-inner">
        {parsed ? (
          <section className="nc-preview" aria-label="Invite found">
            <div className="nc-preview-head">
              <Avatar name={name || parsed.displayName} size={56} />
              <div>
                <p className="nc-kicker">Invite verified ✓</p>
                <p className="muted small">
                  Security code <code>{fingerprintPreview(parsed.messagingPublicKeyHex)}</code>
                </p>
              </div>
            </div>
            <div className="field">
              <label htmlFor="contactName">Name</label>
              <input
                id="contactName"
                value={name}
                maxLength={64}
                onChange={(e) => setName(e.target.value)}
                placeholder="How you'll see them"
                autoComplete="off"
              />
            </div>
            {existing && (
              <p className="muted small" role="status">
                {existing.requestState === "pending"
                  ? `${existing.displayName} already messaged you — adding them accepts their request.`
                  : `Already in your contacts as ${existing.displayName}.`}
              </p>
            )}
            <p className="muted small">
              Their messages are end-to-end encrypted. For extra safety, compare security codes in person
              later (chat details → Verify).
            </p>
            {err && (
              <p className="field-error" role="alert">
                {err}
              </p>
            )}
            <div className="actions-row sticky-actions">
              <button
                className="btn secondary"
                type="button"
                onClick={() => {
                  setParsed(null);
                  setPaste("");
                }}
              >
                Back
              </button>
              <button className="btn" type="button" disabled={!name.trim() || adding} onClick={() => void add()}>
                {adding ? "Adding…" : existing ? "Open chat" : "Add & start chatting"}
              </button>
            </div>
          </section>
        ) : (
          <>
            <section className="nc-card nc-share" aria-label="Invite someone">
              <div className="nc-card-head">
                <span className="nc-icon" aria-hidden>
                  <IconChat size={20} />
                </span>
                <div>
                  <h3>Invite a friend</h3>
                  <p className="muted small">
                    Send your link in any app. When they tap it, you&apos;re connected — no phone number,
                    no sign-up.
                  </p>
                </div>
              </div>
              <div className="nc-actions">
                <button className="btn" type="button" disabled={!myInviteLink} onClick={() => void share()}>
                  Share invite link
                </button>
                <button className="btn secondary" type="button" disabled={!myInviteLink} onClick={() => void copy()}>
                  <IconCopy size={16} /> {copied ? "Copied" : "Copy link"}
                </button>
                <button
                  className="btn secondary"
                  type="button"
                  aria-expanded={showQr}
                  onClick={() => setShowQr((v) => !v)}
                >
                  <IconQr size={16} /> {showQr ? "Hide QR" : "Show my QR"}
                </button>
              </div>
              {showQr && (
                <div className="invite-qr-big">
                  {qr ? <img src={qr} alt="Your Veto invite QR code" /> : <p className="muted">Preparing code…</p>}
                  <p className="muted small">They scan it with their phone camera or in Veto → Scan QR code.</p>
                </div>
              )}
            </section>

            <section className="nc-card" aria-label="Add someone">
              <h3>Got their invite?</h3>
              <div className="nc-actions">
                <button className="btn secondary" type="button" onClick={() => setScanning(true)}>
                  <IconQr size={16} /> Scan QR code
                </button>
              </div>
              <div className="field">
                <label htmlFor="pasteInvite">Paste their invite</label>
                <textarea
                  id="pasteInvite"
                  ref={pasteRef}
                  rows={3}
                  value={paste}
                  onChange={(e) => onPasteChange(e.target.value)}
                  placeholder="Paste the link or message they sent you"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
              </div>
              {err && (
                <p className="field-error" role="alert">
                  {err}
                </p>
              )}
            </section>

            {contacts.length > 0 && onOpenContact && (
              <section className="nc-card" aria-label="Your contacts">
                <h3>Your contacts</h3>
                {contacts.length > 6 && (
                  <div className="nc-search">
                    <IconSearch size={16} />
                    <input
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      placeholder="Search contacts"
                      aria-label="Search contacts"
                    />
                  </div>
                )}
                <ul className="nc-contacts" role="list">
                  {known.map((c) => (
                    <li key={c.id}>
                      <button type="button" className="nc-contact" onClick={() => onOpenContact(c.id)}>
                        <Avatar name={c.displayName} size={36} verified={c.verificationState === "verified"} />
                        <span className="nc-contact-name">{c.displayName}</span>
                        {c.verificationState === "verified" && <span className="nc-badge">verified</span>}
                      </button>
                    </li>
                  ))}
                  {known.length === 0 && <li className="muted small">No match.</li>}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
      {scanning && (
        <InviteScannerModal
          onFound={(text) => {
            setScanning(false);
            setPaste(text);
            apply(text);
          }}
          onClose={() => setScanning(false)}
        />
      )}
    </ModalShell>
  );
}
